import { v4 as uuidv4 } from 'uuid';

async function Send(actualSending, success, failure) {
    try {
        let response = await actualSending();
        if (response.status === 401) {
            throw new Error('Your session has expired, please start a new one');
        }

        if (success) {
            success(await response.json());
        }
    } catch (error) {
        if (failure) {
            failure(error);
        }
        else {
            console.log(error);
        }
    }
}

function constructAcceptanceRequest(body) {
    return {
        request_id: uuidv4(),
        request_body: body
    }
}

function constructSessionIdAsQueryParameter(sessionId) {
    if (sessionId === null || sessionId === undefined) {
        return '';
    }

    return `?session_id=${sessionId}`;
}

const ApiClient = () => {
    const acceptanceBaseUrl = process.env.REACT_APP_API_ENDPOINT;
    const paymentBaseUrl = process.env.REACT_APP_PAYMENT_API_ENDPOINT;

    const getAuthenticationViewPath = "/api/Transaction/authentication/view";
    const getAuthenticationStatusPath = "/api/Transaction/authentication/status";
    const getTransactionSessionInfoPath = "/api/Transaction/session";
    const getPaymentMethodSessionInfoPath = "/api/PaymentMethod/session";
    const payPath = "/api/Transaction/pay";
    const payV2Path = "/api/Transaction/v2/pay";
    const verifyPath = "/api/Transaction/verify";
    const savePaymentMethodPath = "/api/PaymentMethod";
    const applePayStartSessionPath = "/api/payment/apple-pay/transaction/session";
    const cancelInstapayTransactionPath = "/api/transaction/instapay/cancel";
    const getMerchantTransactionReferencePath = "/api/payment/payment-session";
    const getPaymentServiceTransactionIdPath = "/api/payment/transaction/payment-id";
    const getMerchantLogoPath = "/api/Merchant/logo";
    const getMerchantNameEndpoint = "/api/Merchant/name";

    // PaymentLinkGeneration
    const getLiveLinkDetailsPath = "/api/PaymentLinkGeneration/details";
    const initLiveLinkPaymentPath = "/api/PaymentLinkGeneration/payment";
    const updateTransactionMetadataPath = "/api/PaymentLinkGeneration/transaction/metadata";
    const getPaymentLinkDetailsFromTransactionPath = "/api/PaymentLinkGeneration/payment-link/transaction";
    const getPaymentLinkPath = "/api/PaymentLinkGeneration/payment-link";

    const pay = async (requestBody, authToken, success, failure) => {
        return await Send(async () => {
            return await fetch(paymentBaseUrl + payPath, {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                method: 'POST',
                body: JSON.stringify(requestBody),
            });
        }, success, failure);
    };


    const payV2 = async (requestBody, authToken, success, failure) => {
        return await Send(async () => {
            return await fetch(paymentBaseUrl + payV2Path, {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                method: 'POST',
                body: JSON.stringify(requestBody),
            });
        }, success, failure);
    };

    const applePayTransactionStartSession = async (requestBody, authToken, success, failure) => {   
        const request = constructAcceptanceRequest(requestBody);     
        return await Send(async () => {
            return await fetch(acceptanceBaseUrl + applePayStartSessionPath, {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                method: 'POST',
                body: JSON.stringify(request),
            });
        }, success, failure);
    };

    const verify = async (requestBody, authToken, success, failure) => {
        return await Send(async () => {
            return await fetch(paymentBaseUrl + verifyPath, {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                method: 'POST',
                body: JSON.stringify(requestBody),
            });
        }, success, failure);
    };

    const CancelInstapayTransaction = async (authToken, success, failure) => {
        return await Send(async () => {
            return await fetch(paymentBaseUrl + cancelInstapayTransactionPath, {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                method: 'POST',
            });
        }, success, failure);
    };

    const getAuthenticationView = async (requestBody, authToken, success, failure) => {
        return await Send(async () => {
            return await fetch(paymentBaseUrl + getAuthenticationViewPath, {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                method: 'POST',
                body: JSON.stringify(requestBody),
            });
        }, success, failure);
    };

    const getAuthenticationStatus = async (authToken, sessionId, success, failure) => {
        return await Send(async () => {
            return await fetch(paymentBaseUrl + getAuthenticationStatusPath + constructSessionIdAsQueryParameter(sessionId), {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                method: 'GET'
            });
        }, success, failure);
    };

    const savePaymentMethod = async (requestBody, authToken, success, failure) => {
        return await Send(async () => {
            return await fetch(paymentBaseUrl + savePaymentMethodPath, {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                method: 'POST',
                body: JSON.stringify(requestBody),
            });
        }, success, failure);
    };

    const getTransactionSessionInfo = async (authToken, sessionId, success, failure) => {
        return await Send(async () => {
            return await fetch(paymentBaseUrl + getTransactionSessionInfoPath + constructSessionIdAsQueryParameter(sessionId), {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                method: 'GET'
            });
        }, success, failure);
    };

    const getPaymentMethodSessionInfo = async (authToken, sessionId, success, failure) => {
        return await Send(async () => {
            return await fetch(paymentBaseUrl + getPaymentMethodSessionInfoPath + constructSessionIdAsQueryParameter(sessionId), {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                method: 'GET'
            });
        }, success, failure);
    };

    const getLiveLinkDetails = async (profileName, orderRef, success, failure) => {
        return await Send(async () => {
            const params = new URLSearchParams({ order: orderRef});

            return await fetch(`${acceptanceBaseUrl}${getLiveLinkDetailsPath}/${profileName}?${params}`, {
                headers: {
                    'content-type': 'application/json',
                },
                method: 'GET'
            });
        }, success, failure);
    };

    const getPaymentLinkDetailsFromTransaction = async (merchantId, transactionRef, success, failure) => {
        return await Send(async () => {
            return await fetch(`${acceptanceBaseUrl}${getPaymentLinkDetailsFromTransactionPath}/${transactionRef}/${merchantId}`, {
                headers: {
                    'content-type': 'application/json',
                },
                method: 'GET'
            });
        }, success, failure);
    };

    const getPaymentLink = async (requestBody, success, failure) => {
        const params = new URLSearchParams(requestBody);
        return await Send(async () => {
            return await fetch(`${acceptanceBaseUrl}${getPaymentLinkPath}?${params}`, {
                headers: {
                    'content-type': 'application/json'
                },
                method: 'GET',
            });
        }, success, failure);
    };

    const initLiveLinkPayment = async (profileName, requestBody, orderRef, success, failure) => {
        const request = constructAcceptanceRequest(requestBody);
        const params = new URLSearchParams({ order: orderRef});
        return await Send(async () => {
            return await fetch(`${acceptanceBaseUrl}${initLiveLinkPaymentPath}/${profileName}?${params}`, {
                headers: {
                    'content-type': 'application/json'
                },
                method: 'POST',
                body: JSON.stringify(request),
            });
        }, success, failure);
    };

    const getMerchantLogo = async (merchantId, success, failure) => {
        return await Send(async () => {
            return await fetch(`${acceptanceBaseUrl}${getMerchantLogoPath}/${merchantId}`, {
                headers: {
                    'content-type': 'application/json',
                },
                method: 'GET',
            });
        }, success, failure);
    }

    const updateTransactionMetadata = async (authToken, metadata, success, failure) => {
        return await Send(async () => {
            return await fetch(`${acceptanceBaseUrl}${updateTransactionMetadataPath}/`, {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                method: 'PUT',
                body: JSON.stringify(metadata)
            })
        }, success, failure);
    }

    const getMerchantTransactionReference = async (sessionId, success, failure) => {
        return await Send(async () => {
            return await fetch(`${acceptanceBaseUrl}${getMerchantTransactionReferencePath}/${sessionId}`, {
                headers: {
                    'content-type': 'application/json',
                },
                method: 'GET'
            });
        }, success, failure);
    };

    const getPaymentServiceTransactionId = async (merchantId, merchantTransactionReference, success, failure) => {
        return await Send(async () => {
            return await fetch(`${acceptanceBaseUrl}${getPaymentServiceTransactionIdPath}/${merchantTransactionReference}/${merchantId}`, {
                headers: {
                    'content-type': 'application/json',
                },
                method: 'GET'
            });
        }, success, failure);
    };

    const getMerchantName = async (authToken, success, failure) => {
        return await Send(async () => {
            return await fetch(`${acceptanceBaseUrl}${getMerchantNameEndpoint}`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'content-type': 'application/json',
                },
                method: 'GET',
            });
        }, success, failure);
    };

    return {
        pay: pay,
        applePayTransactionStartSession: applePayTransactionStartSession,
        verify: verify,
        getAuthenticationView: getAuthenticationView,
        getAuthenticationStatus: getAuthenticationStatus,
        savePaymentMethod: savePaymentMethod,
        getTransactionSessionInfo: getTransactionSessionInfo,
        getPaymentMethodSessionInfo: getPaymentMethodSessionInfo,
        getLiveLinkDetails: getLiveLinkDetails,
        initLiveLinkPayment: initLiveLinkPayment,
        payV2: payV2,
        getMerchantLogo: getMerchantLogo,
        CancelInstapayTransaction: CancelInstapayTransaction,
        updateTransactionMetaData: updateTransactionMetadata,
        getPaymentLinkDetailsFromTransaction: getPaymentLinkDetailsFromTransaction,
        getMerchantTransactionReference: getMerchantTransactionReference,
        getPaymentLink: getPaymentLink,
        getPaymentServiceTransactionId: getPaymentServiceTransactionId,
        getMerchantName: getMerchantName,
    }
};

export default ApiClient;